import { ChangeDetectionStrategy, Component, DOCUMENT, inject, input, ViewEncapsulation } from '@angular/core';

/**
 * "Skip to main content": the first focusable thing in the app, so a keyboard user can jump past
 * the header (signed-out and mobile) or the sidebar (desktop dashboard) straight to the page.
 *
 * Why a click handler rather than leaving the `href` to the browser: `index.html` sets
 * `<base href="/">`, so a bare `#main-content` resolves to `/#main-content`, a different document
 * than `/dashboard/home#main-content`, and the browser does a full reload of the root route. A
 * `routerLink` with `fragment` avoids the reload but still rewrites the URL (a history entry for a
 * skip) and leaves focus wherever it was: `anchorScrolling` only scrolls. Focus is the point of a
 * skip link, so the handler moves it to the landmark by hand and leaves the route alone. The `href`
 * stays so the element is a real link to assistive technology, and Enter fires `click` on it.
 */
@Component({
  selector: 'app-skip-link',
  template: '<a class="skip-link" [attr.href]="\'#\' + target()" (click)="skip($event)">Skip to main content</a>',
  styleUrl: './skip-link.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SkipLinkComponent {
  private readonly document = inject(DOCUMENT);

  /** The id of the element to land on. It must be focusable, so it carries `tabindex="-1"`. */
  readonly target = input<string>('main-content');

  protected skip(event: MouseEvent): void {
    const target = this.document.getElementById(this.target());
    if (!target) return;
    event.preventDefault();
    target.focus({ preventScroll: true });
    target.scrollIntoView({ block: 'start' });
  }
}
